import { Link, router } from '@inertiajs/react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDeleteLeft, faLayerGroup, faPenToSquare, faPlus, faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { formatDateTime } from "../../Announcements/dates";
import { truncateText, truncateFilename } from "../../../utils/strings";
import RecordsTable, { BulkAction } from './RecordsTable';

export function handleDelete(record: any) {
  const { id, filename } = record;
  if (window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
    router.delete("/admin/attachments/", {
      data: {
        ids: [id]
      }
    });
  }
}

// Handle bulk delete
const handleBulkDelete = (selectedRows: any[], clearSelection: () => void) => {
  const count = selectedRows.length;
  const message = count === 1
    ? `Are you sure you want to delete 1 attachment? This action cannot be undone.`
    : `Are you sure you want to delete ${count} attachments? This action cannot be undone.`;

  if (window.confirm(message)) {
    const ids = selectedRows.map(row => row.id);
    router.delete('/admin/attachments', {
      data: { ids: ids },
      preserveState: true,
      onFinish: () => {
        clearSelection();
      }
    });
  }
};

// Handle copy URLs
const handleCopyUrls = (selectedRows: any[], clearSelection: () => void) => {
  const urls = selectedRows.map(row => row.public_url).join('\n');
  navigator.clipboard.writeText(urls).then(() => {
    alert('URLs copied to clipboard!');
  }).catch((err) => {
    console.error('Failed to copy URLs:', err);
    alert('Failed to copy URLs to clipboard');
  });
};

// Header actions component for attachments mode
function AttachmentsHeaderActions() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      gap: '4px',
    }}>
      <Link
        className="link link-primary"
        style={{ cursor: 'pointer' }}
        href="/admin/attachments/groups"
        preserveState={true}
      >
        <button
          className="btn btn-sm btn-info"
        >
          Groups
          <FontAwesomeIcon icon={faLayerGroup} />
        </button>
      </Link>
      <Link
        className="link link-primary"
        style={{ cursor: 'pointer' }}
        href="/admin/attachments"
        preserveState={true}
        only={['previewed_attachment']}
      >
        <button
          className="btn btn-sm btn-success btn-primary"
        >
          New
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </Link>
    </div>
  );
}

// Bulk actions for attachments mode
const attachmentsBulkActions: BulkAction[] = [
  {
    label: 'Copy URLs',
    className: 'btn btn-sm',
    onClick: handleCopyUrls
  },
  {
    label: 'Delete',
    className: 'btn btn-sm btn-error',
    onClick: handleBulkDelete
  }
];

export default function AttachmentsTable({
  attachments,
  previewedAttachment
}: any) {
  const handleSortChange = (columnName: string, direction: 'asc' | 'desc' | null) => {
    router.visit('', {
      data: {
        order_by: columnName,
        order: direction
      },
      preserveState: true,
      preserveScroll: true,
    })
  };

  const headers = [
    {
      name: "id",
      label: "ID",
      renderCell: (row: any) => (
        <div>
          {row.id}
        </div>
      )
    },
    {
      name: "groups",
      label: "Groups",
      renderCell: (row: any) => (
        <div style={{
          minWidth: '50px',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '4px',
          overflow: 'scroll'
        }}>
          {row.attachments_groups && row.attachments_groups.length > 0 ? (
            row.attachments_groups.map((group: any) => (
              <Link
                key={`${row.id}-group-${group.id}`}
                className="link link-primary"
                style={{
                  backgroundColor: '#570df8',
                  padding: '4px 8px',
                  width: 'fit-content',
                  color: 'white',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
                href={`/admin/attachments/groups/${group.id}`}
                preserveState={true}
              >
                {group.title}
              </Link>
            ))
          ) : (
            <Link
              key={`${row.id}-new-group`}
              href={`/admin/attachments/${row.id}`}
              className="link link-primary"
            >
              Add to group
            </Link>
          )}
        </div >
      )
    },
    {
      name: "attachment_preview",
      label: "Preview",
      renderCell: (row: any) => (
        <div
          style={{
            backgroundImage: `url(${row.public_url})`,
            width: '100%',
            aspectRatio: '1 / 1',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        />
      )
    },
    {
      name: "title",
      label: "Title",
      renderCell: (row: any) => (
        <div style={{
          width: '200px',
          overflow: 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}>
          {truncateText(row.title, 100)}
        </div>
      )
    },
    {
      name: "description",
      label: "Description",
      renderCell: (row: any) => (
        <div style={{
          width: '200px',
          overflow: 'scroll',
        }}>
          {truncateText(row.description, 1000)}
        </div>
      ),
    },
    {
      name: "filename",
      label: "File name",
      renderCell: (row: any) => (
        <div style={{
          minWidth: '150px',
          overflowY: 'auto',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          paddingRight: '4px',
        }}>
          {truncateFilename(row.filename, 20)}
        </div >
      )
    },
    {
      name: "human_readable_size",
      label: "Size",
      renderCell: (row: any) => (
        <div style={{
          width: '75px',
          overflow: 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}>
          {row.human_readable_size}
        </div>
      )
    },
    {
      name: "created_at",
      label: "Created",
      headerStyles: {
        minWidth: '150px'
      },
      renderCell: (row: any) => (
        <div>
          {formatDateTime(row.created_at)}
        </div>
      )
    },
    {
      name: "updated_at",
      label: "Updated",
      headerStyles: {
        minWidth: '150px'
      },
      renderCell: (row: any) => (
        <div>
          {formatDateTime(row.updated_at)}
        </div>
      ),
      renderCellActions: (row: any) => (
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
        }}>
          <Link
            className="btn btn-sm btn-info"
            href={`/admin/attachments/${row.id}`}
            preserveState={true}
            only={['previewed_attachment']}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </Link>
          <div
            className="btn btn-sm btn-error"
            onClick={() => handleDelete(row)}
          >
            <FontAwesomeIcon icon={faDeleteLeft} />
          </div>
          <a
            className="btn btn-sm btn-primary"
            style={{ cursor: 'pointer' }}
            href={row.public_url}
            target="_blank"
          >
            <FontAwesomeIcon icon={faUpRightFromSquare} />
          </a>
        </div>
      )
    },
  ];

  return (
    <RecordsTable
      headers={headers}
      data={attachments.data}
      hasMore={attachments.metadata.has_more}
      recordsCount={attachments.metadata.total}
      recordsCountLabel={attachments.metadata.total === 1 ? 'file' : 'files'}
      secondaryInfo={
        attachments.metadata.byte_size && (
          <span className="text-sm" style={{ marginRight: '8px' }}>
            {attachments.metadata.byte_size}
          </span>
        )
      }
      onSortChange={handleSortChange}
      highlightedRowId={previewedAttachment ? previewedAttachment.id : null}
      searchQuery={attachments.metadata.query}
      sortColumn={attachments.metadata.order_by}
      sortDirection={attachments.metadata.order}
      title="Attachments"
      infiniteScrollDataKey="attachments"
      headerActions={<AttachmentsHeaderActions />}
      bulkActions={attachmentsBulkActions}
    />
  )
}